import { type ApiDeduction, useDeduction } from '@/api/data';
import { T } from '@/i18n';

/**
 * The line Codex specified (2026-09-14): before an employee sends and before a manager
 * approves, the amount the existing calculation will take from the vacation balance,
 * from the server, never computed here. A type that does not deduct shows nothing.
 * `onState` tells the caller whether the amount is resolved so it can hold the action.
 */
export type DeductionState = { status: 'none' | 'loading' | 'error' | 'ready'; days: number | null };

export function useDeductionState(
  p: {
    absence_type_id: number | null;
    start_date?: string;
    end_date?: string;
    request_id?: number | null;
  } | null,
  deductible: boolean,
): DeductionState & { data?: ApiDeduction; refetch: () => void } {
  const q = useDeduction(deductible ? p : null);
  if (!deductible || !p?.absence_type_id || !p.start_date || !p.end_date)
    return { status: 'none', days: null, refetch: () => {} };
  if (q.isPending) return { status: 'loading', days: null, refetch: () => void q.refetch() };
  if (q.isError || !q.data) return { status: 'error', days: null, refetch: () => void q.refetch() };
  return { status: 'ready', days: q.data.days, data: q.data, refetch: () => void q.refetch() };
}

export const deductionLine = (count: number) =>
  count === 1
    ? T('Trekk fra feriesaldo ved godkjenning: {count} dag.', { count })
    : T('Trekk fra feriesaldo ved godkjenning: {count} dager.', { count });
export const chargedLine = (count: number) =>
  count === 1
    ? T('Trukket fra feriesaldo: {count} dag.', { count })
    : T('Trukket fra feriesaldo: {count} dager.', { count });

export function DeductionLine({
  state,
  timed,
  charged,
}: { state: DeductionState & { data?: ApiDeduction }; timed: boolean; charged?: boolean }) {
  if (state.status === 'none') return null;
  return (
    <p className="hint" data-testid="deduction" aria-live="polite" style={{ margin: '4px 0 8px' }}>
      {state.status === 'loading' && T('Henter trekk…')}
      {state.status === 'error' && T('Kunne ikke hente trekket. Prøv igjen.')}
      {state.status === 'ready' && state.days != null && (
        <>
          <b>{charged ? chargedLine(state.days) : deductionLine(state.days)}</b>
          {charged
            ? ` ${T('Det er dette som gis tilbake om fraværet trekkes tilbake eller slettes.')}`
            : state.data?.basis === 'arbeidsdager'
              ? ` ${T('Telles i arbeidsdager, mandag til fredag, uten helligdager.')}`
              : state.data?.basis
                ? ` ${T('Telles i virkedager, mandag til lørdag, uten helligdager.')}`
                : ''}
          {timed ? ` ${T('Denne fraværstypen trekker hele dager, selv om fraværet varer noen timer.')}` : ''}
        </>
      )}
    </p>
  );
}
